/**
 * Ripple splat math (pure, tested). The GL side lives in rippleSim.ts.
 *
 * The height field is simulated on a square grid that covers a square of side `span = max(w, h)` centred on the
 * canvas, so waves travel isotropically regardless of the canvas aspect. Sim UV = (p - centre) / span + 0.5.
 */

export const MAX_DROPS = 24;
/** sub-step spacing along a drag path, in css px */
export const PATH_STEP_PX = 4;

export const DROP = {
  /** dimple held under a finger while it is down (negative = pressed down) */
  dimpleRadius: 0.018, dimpleStrength: -0.035,
  /** continuous drops along the drag path */
  pathRadius: 0.012, pathStrength: 0.05,
  /** first contact */
  downRadius: 0.03, downStrength: 0.22,
  /** larger drop on release */
  upRadius: 0.05, upStrength: 0.35,
  /** first-touch ever: the largest ripple the app makes */
  firstRadius: 0.11, firstStrength: 0.55,
} as const;

export interface SimFrame { cx: number; cy: number; span: number }

export function simFrameFor(width: number, height: number): SimFrame {
  return { cx: width / 2, cy: height / 2, span: Math.max(1, width, height) };
}

export function toSimUv(f: SimFrame, x: number, y: number): [number, number] {
  return [(x - f.cx) / f.span + 0.5, (y - f.cy) / f.span + 0.5];
}

/** Fixed-capacity drop list (u, v, radius, strength) × MAX_DROPS; no allocation after construction. */
export class DropQueue {
  readonly data = new Float32Array(MAX_DROPS * 4);
  count = 0;
  /** drops that did not fit (for diagnostics / tests) */
  dropped = 0;

  push(u: number, v: number, radius: number, strength: number): boolean {
    if (this.count >= MAX_DROPS) { this.dropped++; return false; }
    const o = this.count * 4;
    this.data[o] = u; this.data[o + 1] = v; this.data[o + 2] = radius; this.data[o + 3] = strength;
    this.count++;
    return true;
  }

  clear(): void { this.count = 0; this.dropped = 0; }
}

/**
 * Number of sub-steps for a drag from (x0,y0) to (x1,y1): one every `stepPx`, at least one if the pointer moved.
 */
export function pathStepCount(x0: number, y0: number, x1: number, y1: number, stepPx = PATH_STEP_PX): number {
  const dist = Math.hypot(x1 - x0, y1 - y0);
  if (dist <= 0) return 0;
  return Math.ceil(dist / stepPx);
}

/**
 * Splat small drops evenly along the segment (excluding the start point, including the end point).
 * When the path needs more drops than the queue can take, the drops are decimated evenly (never just truncated),
 * so a fast flick still leaves a trail along its whole length. Returns the number of drops queued.
 */
export function pathSplats(q: DropQueue, f: SimFrame, x0: number, y0: number, x1: number, y1: number, radius = DROP.pathRadius, strength = DROP.pathStrength, stepPx = PATH_STEP_PX): number {
  const n = pathStepCount(x0, y0, x1, y1, stepPx);
  if (n === 0) return 0;
  const room = MAX_DROPS - q.count;
  if (room <= 0) { q.dropped += n; return 0; }
  const take = Math.min(n, room);
  let added = 0;
  for (let i = 1; i <= take; i++) {
    // evenly spaced sample indices in 1..n
    const k = take === n ? i : Math.round((i * n) / take);
    const t = k / n;
    const [u, v] = toSimUv(f, x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
    if (q.push(u, v, radius, strength)) added++;
  }
  return added;
}

/** Wave-stencil constants (Evan Wallace's WebGL water). */
export const WAVE = { stiffness: 2.0, damping: 0.995 } as const;

/**
 * Reference implementation of one stencil step on a CPU grid (used by tests to pin the GLSL semantics):
 * avg of 4 neighbours, vel += (avg - h) * 2, vel *= 0.995, h += vel. Edges clamp like CLAMP_TO_EDGE textures.
 */
export function stepHeightField(h: Float32Array, v: Float32Array, size: number, outH: Float32Array, outV: Float32Array): void {
  for (let y = 0; y < size; y++) {
    const ym = Math.max(0, y - 1) * size, yp = Math.min(size - 1, y + 1) * size, y0 = y * size;
    for (let x = 0; x < size; x++) {
      const xm = Math.max(0, x - 1), xp = Math.min(size - 1, x + 1);
      const avg = (h[y0 + xm]! + h[y0 + xp]! + h[ym + x]! + h[yp + x]!) * 0.25;
      let vel = v[y0 + x]! + (avg - h[y0 + x]!) * WAVE.stiffness;
      vel *= WAVE.damping;
      outV[y0 + x] = vel;
      outH[y0 + x] = h[y0 + x]! + vel;
    }
  }
}

/** Drop profile at normalised distance t = dist / radius: raised cosine, 1 at the centre, 0 at the rim. */
export function dropProfile(t: number): number {
  const d = Math.max(0, 1 - t);
  return 0.5 - Math.cos(d * Math.PI) * 0.5;
}
